import { useId } from 'react'
import type { ReactElement } from 'react'
import { PhonePromptCard } from './PhonePromptCard'
import { PROMPT_COPY } from './promptCopy'
import type { StoreRating } from './storeRating'
import styles from './phonePromptText.module.css'

/**
 * Phone, route page, variant B (Moovit-like) — Figma 9336:77271 /
 * 9346:77587: the store rating, the destination in the accent colour. No
 * rating for the visitor's store → the rating block is left out rather than
 * faked (brief: the rating is a variable, never a hard-coded number).
 *
 * The destination's hyphens are non-breaking: "Harrow-on-the-Hill" or
 * "Bromley-by-Bow" moves to the next line whole instead of splitting into
 * "Harrow-on-the-" / "Hill". Spaces still wrap, so the longest name
 * ("Cutty Sark (for Maritime Greenwich)") fits a 375 pt phone.
 */
const NON_BREAKING_HYPHEN = '\u2011'

export function PhoneRouteBPrompt({
  destination,
  rating,
  storeHref,
  onGetTheApp,
  onBack,
}: {
  readonly destination: string
  readonly rating: StoreRating | undefined
  readonly storeHref: string
  readonly onGetTheApp: () => void
  readonly onBack: () => void
}): ReactElement {
  const titleId = useId()
  return (
    <PhonePromptCard
      labelledBy={titleId}
      placement="bottom"
      icon="small"
      contentTop={51}
      primary={{ label: PROMPT_COPY.getTheApp }}
      primaryHref={storeHref}
      secondary={{ label: PROMPT_COPY.backToWeb }}
      order="primaryFirst"
      onPrimary={onGetTheApp}
      onSecondary={onBack}
    >
      {rating && (
        <div className={styles.rating}>
          <p className={styles.stars} aria-hidden="true">
            ★★★★★
          </p>
          <p className={styles.ratings}>
            {PROMPT_COPY.routeB.ratings(rating.average, rating.count)}
          </p>
        </div>
      )}
      <h2 id={titleId} className={styles.destinationTitle}>
        {PROMPT_COPY.routeB.titleBefore}
        <span className={styles.destination}>
          {destination.replaceAll('-', NON_BREAKING_HYPHEN)}
        </span>
        {PROMPT_COPY.routeB.titleAfter}
      </h2>
      <p className={styles.subtitle}>{PROMPT_COPY.routeB.subtitle}</p>
    </PhonePromptCard>
  )
}
